import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import {
  useAddInspectionsVehicleDamageActions,
  useDeleteInspectionsVehicleDamageAction,
  useInspectionSearchFlatRateCode,
} from '@/api/Inspections/hooks/useInspectionsVehicles';
import { useTranslate } from '@/translations';

import {
  Alert,
  App,
  AutoComplete,
  Button,
  Card,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Table,
} from 'antd';
import {
  DamageActionData,
  Damage,
  FlatRate,
  FrCode,
  DamageAction,
} from '@/types';
import {
  faAdd,
  faEdit,
  faMinus,
  faTrash,
  faXmark,
  Icon,
} from '@/ui/components';
import { ColumnsType } from 'antd/es/table';
import { CreateFlatRateCodePopup } from './CreateFlatRateCodePopup';
import { StyledLink } from '@/ui/globalStyles';
import { currencyFormatted } from '@/utils/helpers';
import { DamagePartsGrid } from '../DamageParts/DamagePartsGrid';
import { useAppCustomers } from '@/hooks';
import { getActionParts } from './utils';
import { getDamageLineOptions } from './DamageLineOptions';
import {
  buildSearchKey,
  getCachedOrFetchList,
  getNextRowRequestId,
  isLatestRowRequest,
} from '../searchRequestUtils';
import { useRefreshDamageLines } from '../useRefreshDamageLines';
import {
  isExistingRowEditing,
  patchEditingRow,
  removeEditingRow,
  upsertEditingRow,
  withEditingRows,
} from '../inlineRowEdit';
import { isDropdownOpen } from '../inlineCellNavigation';
import { InlineNavCell } from '../InlineNavCell';
import { useInlineCellNavigation } from '../useInlineCellNavigation';
import {
  addFrShortcutHint,
  isAddFrShortcut,
  isSaveNotesShortcut,
  saveNotesShortcutHint,
} from '../preTimingShortcuts';
import { useInlineAddShortcut } from '../useInlineAddShortcut';

interface DamageActionsGridProps {
  title?: string;
  canEditFr?: boolean;
  canEditParts?: boolean;
  canDeleteExisting?: boolean;
  inspectionId: number;
  damageLine?: {
    damageLineId: number;
    dataSource: DamageAction[];
  };
  damageLines?: Damage[];
  preferredDamageLineIds?: number[];
  autoFocusCode?: boolean;
  damageLinesLoading?: boolean;
  onSubmit: () => void | Promise<unknown>;
}
export const DamageActionsGrid: FC<DamageActionsGridProps> = ({
  title,
  canEditFr,
  canEditParts,
  canDeleteExisting,
  inspectionId,
  damageLine,
  damageLines,
  preferredDamageLineIds,
  autoFocusCode,
  damageLinesLoading,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const { message, modal } = App.useApp();
  const { hasDamageApproveAccess } = useAppCustomers();
  const tableRef = useRef<HTMLDivElement>(null);
  useInlineCellNavigation(tableRef, !!canEditFr);

  const { mutateAsync: fetchFlatRates, isPending: flatRatesLoading } =
    useInspectionSearchFlatRateCode();
  const flatRateSearchSeqByRowRef = useRef<Record<number, number>>({});
  const flatRateSearchCacheRef = useRef(new Map<string, FrCode[]>());
  const flatRateSearchInFlightRef = useRef(
    new Map<string, Promise<{ data?: FrCode[] }>>(),
  );

  const [createFRCodeModalOpen, setCreateFRCodeModalOpen] = useState(false);
  const [flatRateMap, setFlatRateMap] = useState<Record<string, FrCode[]>>({});
  const setFlatRates = (id: number, options: FrCode[]) => {
    setFlatRateMap((prev) => ({
      ...prev,
      [id]: options,
    }));
  };

  const [editingRows, setEditingRows] = useState<DamageActionData[]>([]);

  const [damageFlatRates, setDamageFlatRates] = useState<DamageActionData[]>(
    [],
  );
  const { snapshot, refreshing, refreshDamageLines } =
    useRefreshDamageLines<DamageActionData>();
  const list: DamageActionData[] = (
    damageLine
      ? damageLine.dataSource.map(
          (o, idx) =>
            ({
              id: idx,
              actionId: o.id,
              referenceId: o.referenceId,
              code: o.flatRateCode,
              description: o.comment || o.flatRateDesc,
              quantity: o.quantity,
              price: hasDamageApproveAccess ? o.retailCost : o.cost,
              lineCost: hasDamageApproveAccess ? o.retailLineCost : o.lineCost,
              damageLineIds: [damageLine.damageLineId],
            }) as DamageActionData,
        )
      : []
  ).concat(damageFlatRates);

  const [error, setError] = useState<string>();

  const {
    mutateAsync: addDamageActionsAsync,
    isPending: addDamageActionsLoading,
  } = useAddInspectionsVehicleDamageActions();

  const { mutateAsync: deleteDamageActionAsync } =
    useDeleteInspectionsVehicleDamageAction();

  const getDefaultFlatRate = useCallback(
    (id: number) =>
      ({
        id: id,
        referenceId: 0,
        code: '',
        description: '',
        quantity: 1,
        price: 0,
        damageLineIds: damageLine?.damageLineId
          ? [damageLine.damageLineId]
          : preferredDamageLineIds || [],
      }) as DamageActionData,
    [damageLine?.damageLineId, preferredDamageLineIds],
  );

  const resetRecords = useCallback(() => {
    setFlatRateMap({});
    setDamageFlatRates(damageLine?.damageLineId ? [] : [getDefaultFlatRate(0)]);
    setEditingRows([]);
  }, [damageLine?.damageLineId, getDefaultFlatRate]);

  useEffect(() => {
    resetRecords();
  }, [resetRecords]);

  useEffect(() => {
    if (!autoFocusCode) return;
    const timer = window.setTimeout(() => {
      tableRef.current
        ?.querySelector<HTMLInputElement>('[data-pt-col="code"] input')
        ?.focus();
    }, 50);
    return () => window.clearTimeout(timer);
  }, [autoFocusCode]);

  const onAdd = useCallback(
    (data?: DamageActionData) => {
      const copyList = [...damageFlatRates];
      const nextId =
        copyList.length > 0
          ? Math.max(...copyList.map((o) => o.id)) + 1
          : damageLine?.dataSource.length || 0;
      if (data) data.id = nextId;
      copyList.push(data || getDefaultFlatRate(nextId));
      setDamageFlatRates(copyList);
    },
    [damageFlatRates, damageLine?.dataSource.length, getDefaultFlatRate],
  );
  const frAddRef = useInlineAddShortcut(
    'fr',
    isAddFrShortcut,
    () => onAdd(),
    !!canEditFr,
  );

  const onDelete = (id: number, exist: boolean) => {
    if (exist) {
      modal.confirm({
        title: 'Confirm',
        content: t('deleteRecordQuestion'),
        okText: t('yes'),
        onOk: async () => {
          const res = await deleteDamageActionAsync(id);
          if (res.message) {
            message.error(res.message.Message);
          } else {
            onSubmit();
          }
        },
        onCancel: () => {},
      });
    } else {
      const copyList = [...damageFlatRates].filter((o) => o.id !== id);
      setDamageFlatRates(copyList);
    }
  };

  const onChangeValue = (
    id: number,
    field: keyof DamageActionData,
    value: any,
  ) => {
    setError(undefined);
    if (editingRows.some((row) => row.id === id && row.actionId)) {
      setEditingRows((current) =>
        patchEditingRow(current, id, { [field]: value }),
      );
      return;
    }
    const copyList = [...damageFlatRates];
    const part: any = copyList.find((o) => o.id === id);
    if (part) {
      part[field] = value;
      setDamageFlatRates(copyList);
    }
  };

  const handleSearch = async (id: number, flatRateCode: string) => {
    setFlatRates(id, []);
    if (!flatRateCode || flatRateCode.length < 5) {
      return;
    }

    const normalizedCode = flatRateCode.trim();
    const searchKey = buildSearchKey(inspectionId, normalizedCode);
    const requestId = getNextRowRequestId(flatRateSearchSeqByRowRef, id);

    const frList = await getCachedOrFetchList({
      key: searchKey,
      cacheRef: flatRateSearchCacheRef,
      inFlightRef: flatRateSearchInFlightRef,
      fetcher: () =>
        fetchFlatRates({
          inspectionId,
          flatRateCode: normalizedCode,
        }),
    });
    if (!isLatestRowRequest(flatRateSearchSeqByRowRef, id, requestId)) return;

    setFlatRates(id, frList);
    const fr = frList.find(
      (o) => o.flatRate?.toLowerCase() === normalizedCode.toLowerCase(),
    );
    if (fr) {
      onChangeFlateRate(id, fr);
    }
  };

  const onChangeFlateRate = (id: number, selectedOption: FrCode) => {
    if (editingRows.some((row) => row.id === id && row.actionId)) {
      setEditingRows((current) =>
        current.map((row) =>
          row.id === id && row.actionId
            ? {
                ...row,
                referenceId: selectedOption.id,
                code: selectedOption.flatRate,
                description: selectedOption.flatRateDesc,
                quantity: 1,
                price: selectedOption.fixedLabourCost,
              }
            : row,
        ),
      );
      return;
    }
    const copyList = [...damageFlatRates];
    const item = copyList.find((o) => o.id === id);
    if (item) {
      item.referenceId = selectedOption.id;
      item.code = selectedOption.flatRate;
      item.description = selectedOption.flatRateDesc;
      item.quantity = 1;
      item.price = selectedOption.fixedLabourCost;
      setDamageFlatRates(copyList);
    }
  };

  const onSave = async () => {
    if (damageFlatRates.some((o) => !o.referenceId)) {
      setError('Some of the code not choosed');
      return;
    }
    if (
      damageFlatRates.some((o) => o.quantity < 0) ||
      editingRows.some((o) => o.quantity < 0)
    ) {
      setError('The quantity must be a non-negative number');
      return;
    }
    if (
      damageFlatRates.some((o) => o.price < 0) ||
      editingRows.some((o) => o.price < 0)
    ) {
      setError('The cost must be a non-negative number');
      return;
    }
    if (damageFlatRates.some((o) => o.damageLineIds.length == 0)) {
      setError('Please choose the damage lines');
      return;
    }
    if (editingRows.some((o) => !o.referenceId)) {
      setError('Please choose the code');
      return;
    }

    const res = await addDamageActionsAsync({
      inspectionId,
      type: 'FR',
      items: [
        ...editingRows.map((row) => ({
          id: row.actionId,
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageLineIds: row.damageLineIds,
        })),
        ...damageFlatRates.map((row) => ({
          referenceId: row.referenceId,
          description: row.description,
          quantity: row.quantity,
          price: row.price,
          damageLineIds: row.damageLineIds,
        })),
      ],
    });
    if (!res.success) {
      setError(res.message.Message);
      return;
    }

    const heldRows = withEditingRows(
      list,
      editingRows.map((row) => ({
        ...row,
        lineCost: (row.price || 0) * (row.quantity || 0),
      })),
      'actionId',
    );
    await refreshDamageLines(heldRows, onSubmit, resetRecords);
  };

  const readOnly = (record: DamageActionData) =>
    !canEditFr ||
    (!!record.actionId &&
      !isExistingRowEditing(record, editingRows, 'actionId'));
  const tableRows = withEditingRows(
    snapshot ?? list,
    editingRows.map((row) => ({
      ...row,
      lineCost: (row.price || 0) * (row.quantity || 0),
    })),
    'actionId',
  );

  const onCreateFrCodeCallback = useCallback(
    (data: FlatRate) => {
      if (!data.id) return;

      onAdd({
        id: 0,
        referenceId: data.id,
        code: data.flatRate,
        description: data.description,
        quantity: 1,
        price: data.price,
        lineCost: data.price,
        damageLineIds: [],
      });
    },
    [onAdd],
  );

  const columns: ColumnsType<DamageActionData> = [
    {
      title: t('code'),
      width: 160,
      render: (_, record) =>
        readOnly(record) ? (
          record.code
        ) : (
          <InlineNavCell row={record.id} col="code">
            <AutoComplete
              className="w-100"
              value={record.code}
              options={(flatRateMap[record.id] || []).map((o) => ({
                value: o.id,
                label: o.flatRate,
              }))}
              onSearch={(e) => {
                onChangeValue(record.id, 'referenceId', 0);
                onChangeValue(record.id, 'code', e);
                handleSearch(record.id, e);
              }}
              onSelect={(_, option) => {
                const options = flatRateMap[record.id] || [];
                const selectedOption = options.find(
                  (o) => o.id === option.value,
                );
                if (selectedOption) {
                  onChangeFlateRate(record.id, selectedOption);
                }
              }}
              notFoundContent={
                flatRatesLoading ? <Spin size="small" /> : 'No results'
              }
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('description'),
      width: 200,
      render: (_, record) =>
        readOnly(record) ? (
          record.description
        ) : (
          <InlineNavCell row={record.id} col="notes">
            <Input.TextArea
              rows={1}
              value={record.description}
              onChange={(e) =>
                onChangeValue(record.id, 'description', e.target.value)
              }
              placeholder={t('shortcutSaveNotes', {
                shortcut: saveNotesShortcutHint(),
              })}
              onKeyDown={(event) => {
                if (!isSaveNotesShortcut(event.nativeEvent)) return;
                event.preventDefault();
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('quantity'),
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          record.quantity
        ) : (
          <InlineNavCell row={record.id} col="quantity">
            <InputNumber
              value={record.quantity}
              onChange={(e) => onChangeValue(record.id, 'quantity', e || 1)}
              onPressEnter={() => {
                if (isDropdownOpen()) return;
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('cost'),
      width: 100,
      render: (_, record) =>
        readOnly(record) ? (
          currencyFormatted(record.price)
        ) : (
          <InlineNavCell row={record.id} col="cost">
            <InputNumber
              prefix="£"
              value={record.price}
              onChange={(e) => onChangeValue(record.id, 'price', e || 0)}
              onPressEnter={() => {
                if (isDropdownOpen()) return;
                void onSave();
              }}
            />
          </InlineNavCell>
        ),
    },
    {
      title: t('lineCost'),
      width: 100,
      render: (_, record) => currencyFormatted(record.lineCost),
    },
    ...(damageLine
      ? []
      : [
          {
            title: 'Damages',
            width: 300,
            render: (_: any, record: DamageActionData) => (
              <InlineNavCell row={record.id} col="damages">
                <Select
                  className="w-100"
                  showSearch
                  allowClear
                  mode="multiple"
                  options={getDamageLineOptions(
                    damageLines || [],
                    record.code,
                    'FR',
                  )}
                  value={record.damageLineIds}
                  onChange={(value) => {
                    onChangeValue(record.id, 'damageLineIds', value || []);
                  }}
                />
              </InlineNavCell>
            ),
          },
        ]),
    ...(!canEditFr && !canDeleteExisting
      ? []
      : [
          {
            dataIndex: 'id',
            width: 88,
            render: (_: any, record: DamageActionData) => (
              <Space>
                {!canEditFr ? (
                  record.actionId ? (
                    <StyledLink
                      onClick={() => onDelete(record.actionId || 0, true)}
                    >
                      <Icon icon={faTrash} />
                    </StyledLink>
                  ) : null
                ) : record.actionId ? (
                  isExistingRowEditing(record, editingRows, 'actionId') ? (
                    <StyledLink
                      onClick={() => {
                        setError(undefined);
                        setEditingRows((current) =>
                          removeEditingRow(
                            current,
                            record.actionId,
                            'actionId',
                          ),
                        );
                      }}
                    >
                      <Icon icon={faXmark} />
                    </StyledLink>
                  ) : (
                    <>
                      <StyledLink
                        onClick={() =>
                          setEditingRows((current) =>
                            upsertEditingRow(current, record, 'actionId'),
                          )
                        }
                      >
                        <Icon icon={faEdit} />
                      </StyledLink>
                      <StyledLink
                        onClick={() => onDelete(record.actionId || 0, true)}
                      >
                        <Icon icon={faTrash} />
                      </StyledLink>
                    </>
                  )
                ) : (
                  <Button
                    size="small"
                    shape="circle"
                    color="danger"
                    variant="outlined"
                    onClick={() => onDelete(record.id, false)}
                  >
                    <Icon icon={faMinus} />
                  </Button>
                )}
              </Space>
            ),
          },
        ]),
  ];

  return (
    <div ref={frAddRef} data-inline-add="fr">
      <Spin
        spinning={addDamageActionsLoading || refreshing || !!damageLinesLoading}
      >
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="m-0 text-uppercase">Repair Operations ({title})</h6>
          {canEditFr ? (
            <div>
              <Button onClick={() => setCreateFRCodeModalOpen(true)}>
                Add FR Code
              </Button>
              <Button
                className="mx-3"
                title={addFrShortcutHint()}
                shape="circle"
                onClick={() => onAdd()}
              >
                <Icon icon={faAdd} />
              </Button>
              <Button
                type="primary"
                title={saveNotesShortcutHint()}
                disabled={
                  addDamageActionsLoading ||
                  (damageFlatRates.length === 0 && editingRows.length === 0)
                }
                onClick={onSave}
              >
                {t('save')}
              </Button>
            </div>
          ) : null}
        </div>

        <div ref={tableRef}>
          <Table
            columns={columns}
            dataSource={tableRows}
            rowKey="id"
            expandable={
              damageLine
                ? {
                    rowExpandable: (data) => (data.actionId ? true : false),
                    expandedRowRender: (data) => (
                      <Card>
                        <DamagePartsGrid
                          title={t('partsFludids')}
                          disabled={!canEditParts}
                          canDeleteExisting={canDeleteExisting}
                          inspectionId={inspectionId}
                          damageActionId={data.actionId!}
                          damagePartsData={getActionParts(
                            damageLine?.dataSource,
                            data.actionId,
                          )}
                          damageLinesLoading={damageLinesLoading}
                          onSubmit={onSubmit}
                        />
                      </Card>
                    ),
                  }
                : undefined
            }
            pagination={false}
          />
        </div>

        {error ? <Alert message={error} type="error" className="mt-3" /> : null}
      </Spin>

      <CreateFlatRateCodePopup
        inspectionId={inspectionId}
        isPopupVisible={createFRCodeModalOpen}
        togglePopup={setCreateFRCodeModalOpen}
        onSubmit={onCreateFrCodeCallback}
      />
    </div>
  );
};
